/**
 * Hero Slider JavaScript
 * Enhanced hero slider with glassmorphism design and smooth animations
 */

document.addEventListener('DOMContentLoaded', function () {
    // Get all necessary DOM elements
    const slides = document.querySelectorAll('.hero-slide');
    const dots = document.querySelectorAll('.hero-dot');
    const leftArrow = document.querySelector('.hero-arrow.left');
    const rightArrow = document.querySelector('.hero-arrow.right');
    const wrapper = document.querySelector('.hero-slider-wrapper');
    const currentSlideText = document.querySelector('.current-slide');
    const progressFill = document.querySelector('.progress-fill');
    
    // Slider configuration
    let current = 0;
    let timer;
    let progressTimer;
    let duration = 15000; // 15 seconds per slide (increased from 5 seconds)
    let isPaused = false;

    /**
     * Show specific slide by index
     * @param {number} index - Index of slide to show
     */
    function show(index) {
        // Update active states for slides and dots
        slides.forEach((slide, i) => {
            slide.classList.toggle('active', i === index);
            dots[i]?.classList.toggle('active', i === index);
        });
        
        // Update slide counter text
        if (currentSlideText) {
            currentSlideText.textContent = index + 1;
        }
        
        current = index;
        
        // Start progress animation if not paused
        if (!isPaused) {
            animateProgress();
        }
    }

    /**
     * Animate the progress bar for current slide
     */
    function animateProgress() {
        if (progressFill) {
            progressFill.style.width = '0%';
            
            let start = null;
            
            function animate(time) {
                if (!start) start = time;
                let elapsed = time - start;
                let progress = Math.min(elapsed / duration, 1);
                
                progressFill.style.width = (progress * 100) + '%';
                
                if (progress < 1 && !isPaused) {
                    progressTimer = requestAnimationFrame(animate);
                }
            }
            
            cancelAnimationFrame(progressTimer);
            requestAnimationFrame(animate);
        }
    }

    /**
     * Move to next slide
     */
    function next() {
        show((current + 1) % slides.length);
    }

    /**
     * Move to previous slide
     */
    function prev() {
        show((current - 1 + slides.length) % slides.length);
    }

    /**
     * Start automatic slideshow
     */
    function startAutoplay() {
        stopAutoplay();
        isPaused = false;
        animateProgress();
        timer = setInterval(next, duration);
    }

    /**
     * Stop automatic slideshow
     */
    function stopAutoplay() {
        clearInterval(timer);
        cancelAnimationFrame(progressTimer);
        isPaused = true;
        
        // Reset progress bar
        if (progressFill) {
            progressFill.style.width = '0%';
        }
    }

    // Event Listeners for Navigation

    /**
     * Right arrow click handler
     */
    rightArrow?.addEventListener('click', () => {
        next();
        startAutoplay();
    });

    /**
     * Left arrow click handler
     */
    leftArrow?.addEventListener('click', () => {
        prev();
        startAutoplay();
    });

    /**
     * Dot navigation click handlers
     */
    dots.forEach((dot, index) =>
        dot.addEventListener('click', () => {
            show(index);
            startAutoplay();
        })
    );

    // Touch/Swipe Support for Mobile

    let startX = 0;
    let startY = 0;
    
    /**
     * Touch start handler
     */
    wrapper.addEventListener('touchstart', e => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
    });

    /**
     * Touch end handler with swipe detection
     */
    wrapper.addEventListener('touchend', e => {
        let endX = e.changedTouches[0].clientX;
        let endY = e.changedTouches[0].clientY;
        let deltaX = startX - endX;
        let deltaY = Math.abs(startY - endY);
        
        // Only trigger swipe if horizontal movement is greater than vertical
        // and the swipe distance is at least 50px
        if (Math.abs(deltaX) > deltaY && Math.abs(deltaX) > 50) {
            if (deltaX > 0) {
                next(); // Swipe left - go to next slide
            } else {
                prev(); // Swipe right - go to previous slide
            }
            startAutoplay();
        }
    });

    // Desktop Mouse Interaction

    /**
     * Pause autoplay on hover (desktop only)
     */
    if (window.matchMedia('(min-width: 769px)').matches) {
        wrapper.addEventListener('mouseenter', stopAutoplay);
        wrapper.addEventListener('mouseleave', startAutoplay);
    }

    // Keyboard Navigation

    /**
     * Keyboard navigation handler
     */
    document.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
            prev();
            startAutoplay();
        } else if (e.key === 'ArrowRight') {
            next();
            startAutoplay();
        } else if (e.key === ' ') {
            // Spacebar to pause/resume
            e.preventDefault();
            if (isPaused) {
                startAutoplay();
            } else {
                stopAutoplay();
            }
        }
    });

    // Visibility API - Pause when tab is not visible

    /**
     * Handle page visibility changes
     */
    document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
            stopAutoplay();
        } else if (!isPaused) {
            startAutoplay();
        }
    });

    // Intersection Observer - Pause when slider is not in view

    /**
     * Intersection observer for performance optimization
     */
    if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    if (!isPaused) startAutoplay();
                } else {
                    stopAutoplay();
                }
            });
        }, {
            threshold: 0.5 // Start/stop when 50% of slider is visible
        });

        observer.observe(wrapper);
    }

    // Initialize the slider

    /**
     * Initialize slider on page load
     */
    function init() {
        // Ensure at least one slide exists
        if (slides.length === 0) {
            console.warn('Hero Slider: No slides found');
            return;
        }

        // Show first slide and start autoplay
        show(current);
        startAutoplay();
    }

    // Start the slider
    init();

    // Public API (optional - for external control)
    window.HeroSlider = {
        next: next,
        prev: prev,
        goTo: show,
        play: startAutoplay,
        pause: stopAutoplay,
        getCurrentSlide: () => current,
        getTotalSlides: () => slides.length
    };
});